import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import logo from "./assets/images/logo.svg";
import whiteLogo from "./assets/images/logo-white.svg";
import whatsapp_icon from "./assets/images/whatsapp.svg";
import "./App.scss";
import UploadField from "./uploadField";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Fade, Slide } from "react-awesome-reveal";
import heroImage from "./assets/images/hero-car.png";

// importing the car brands icons
import audi from "./assets/images/cars/audi.svg";
import ford from "./assets/images/cars/ford.svg";
import toyota from "./assets/images/cars/toyota.svg";
import honda from "./assets/images/cars/honda.svg";
import tesla from "./assets/images/cars/tesla.svg";
import nissan from "./assets/images/cars/nissan.svg";
import volkswagen from "./assets/images/cars/volkswagen.svg";

// importing the images for the how it works section
import calendar from "./assets/images/calendar.svg";

// importing the images for the why us section
import stars from "./assets/images/stars.svg";
import coins from "./assets/images/coins.svg";
import check from "./assets/images/check.svg";

// Simple counter component for animating numbers when inView is true
function Counter({ end, duration = 1500, inView }) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!inView) {
      setValue(0); // reset when out of view
      return;
    }

    const endValue = Number(end) || 0;
    if (endValue === 0) {
      setValue(0);
      return;
    }

    const startTime = performance.now();

    const step = (currentTime) => {
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const current = Math.floor(progress * endValue);
      setValue(current);
      if (progress < 1) requestAnimationFrame(step);
    };

    const id = requestAnimationFrame(step);
    return () => cancelAnimationFrame(id);
  }, [end, duration, inView]);

  return <span>{value}</span>;
}

function App() {
  // Scroll to top on reload/mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeLink, setActiveLink] = useState("#home"); // State to track active link
  const [whyUsInView, setWhyUsInView] = useState(false);
  const whyUsRef = useRef(null);
  const notify = () => toast("Form submitted successfully!");

  const handleSetActive = (link) => {
    setActiveLink(link);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === whyUsRef.current) {
            if (entry.isIntersecting) {
              setWhyUsInView(true);
            } else {
              setWhyUsInView(false);
            }
          }
        });
      },
      { threshold: 0.3 }
    );

    const target = whyUsRef.current; // capture current ref value

    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) observer.unobserve(target);
      observer.disconnect();
    };
  }, []);

  const markeData = [
    {
      name: "Toyota",
      image: toyota,
    },
    {
      name: "Ford",
      image: ford,
    },
    {
      name: "Tesla",
      image: tesla,
    },
    {
      name: "Volkswagen",
      image: volkswagen,
    },
    {
      name: "Honda",
      image: honda,
    },
    {
      name: "Nissan",
      image: nissan,
    },
    {
      name: "Audi",
      image: audi,
    },
  ];

  const howItWorksData = [
    {
      title: "Höchste Qualität",
      description:
        "Sorgfältig ausgewählte Fahrzeuge, geprüft auf Zuverlässigkeit, Leistung und Stil.",
      image: stars,
      alt: "search",
    },
    {
      title: "Beste Preise",
      description:
        "Unschlagbare Angebote mit voller Transparenz - keine versteckten Gebühren.",
      image: coins,
      alt: "calendar",
    },
    {
      title: "Einfacher Ablauf",
      description:
        "Vom Suchen bis zum Kauf - alles reibungslos und stressfrei.",
      image: check,
      alt: "happy-face",
    },
    {
      title: "24/7 Unterstützung",
      description:
        "Echte Menschen, jederzeit bereit zu helfen - wann immer Sie uns brauchen.",
      image: calendar,
      alt: "search",
    },
  ];

  const whyUsData = [
    {
      title: 25,
      subtitle: "Jahre an Erfahrung",
      description:
        "Wir bieten eine große Auswahl an hochwertigen Fahrzeugen, darunter Luxusautos, SUVs, Vans und vieles mehr.",
    },
    {
      title: 1000,
      subtitle: "Verkaufte Fahrzeuge",
      description:
        "Unsere Preise sind äußerst wettbewerbsfähig, sodass Sie Top-Angebote für Ihr Auto erhalten und gleichzeitig sicher sein können, dass Sie den besten Gegenwert für Ihr Fahrzeug bekommen.",
    },
    {
      title: 615,
      subtitle: "Zufriedene Kunden",
      description:
        "Unser einfaches und effizientes Online-System macht den Verkauf Ihres Autos schnell und bequem, mit einfachen Schritten, die Sie jederzeit und von überall aus starten können.",
    },
  ];

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const renderForm = () => {
    // Pass setUploadedFile to UploadField so it can update the file state
    return (
      <Fade triggerOnce>
        <div className="form-container">
          <ToastContainer />
          <form
            onSubmit={handleSubmit((data) => {
              const formData = new FormData();

              // Append text fields (skip the file field!)
              Object.entries(data).forEach(([key, value]) => {
                if (key !== "fahrzeugbilder_hinzufügen") {
                  formData.append(key, value ?? "");
                }
              });

              // Append ALL files from RHF (FileList)
              const files = data.fahrzeugbilder_hinzufügen;
              if (files && files.length) {
                Array.from(files).forEach((file) => {
                  formData.append("fahrzeugbilder_hinzufügen[]", file); // <-- array key
                });
              }

              axios
                .post(
                  "https://cms.tarraf-auto-handel.ch/api/contact/send",
                  formData,
                  {
                    headers: { Accept: "application/json" }, // DO NOT set Content-Type, let Axios set it
                  }
                )
                .then(() => {
                  notify();
                  reset();
                })
                .catch(() =>
                  alert(
                    "There was an error submitting the form. Please try again."
                  )
                );
            })}
          >
            <div className="form-group">
              <label htmlFor="name">Name des Fahrzeugs</label>
              <input
                id="name"
                {...register("name", { required: "Name is required" })}
              />
              {errors.name && <p className="error">{errors.name.message}</p>}
            </div>

            <div className="form-group">
              <label htmlFor="email">E-Mail</label>
              <input
                type="email"
                id="email"
                autoComplete="email"
                {...register("email", {
                  required: "E-Mail ist erforderlich",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Bitte eine gültige E-Mail eingeben",
                  },
                })}
              />
              {errors.email && <p className="error">{errors.email.message}</p>}
            </div>

            <div className="form-group">
              <label htmlFor="fahrzeugkategorie">Fahrzeugkategorie</label>
              <input
                id="fahrzeugkategorie"
                {...register("fahrzeugkategorie", {
                  required: "Fahrzeugkategorie is required",
                })}
              />
              {errors.fahrzeugkategorie && (
                <p className="error">{errors.fahrzeugkategorie.message}</p>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="getriebe">Getriebe</label>
              <input
                id="getriebe"
                {...register("getriebe", { required: "Getriebe is required" })}
              />
              {errors.getriebe && (
                <p className="error">{errors.getriebe.message}</p>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="marke">Marke</label>
              <input
                id="marke"
                {...register("marke", { required: "Marke is required" })}
              />
              {errors.marke && <p className="error">{errors.marke.message}</p>}
            </div>

            <div className="form-group">
              <label htmlFor="stoff">Stoff</label>
              <input
                id="stoff"
                {...register("stoff", { required: "Stoff is required" })}
              />
              {errors.stoff && <p className="error">{errors.stoff.message}</p>}
            </div>

            <div className="form-group">
              <label htmlFor="aufbau">Aufbau</label>
              <input
                id="aufbau"
                {...register("aufbau", { required: "Aufbau is required" })}
              />
              {errors.aufbau && (
                <p className="error">{errors.aufbau.message}</p>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="hubraum">Hubraum</label>
              <input
                id="hubraum"
                {...register("hubraum", { required: "Hubraum is required" })}
              />
              {errors.hubraum && (
                <p className="error">{errors.hubraum.message}</p>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="fahrzeugbeschreibung">Fahrzeugbeschreibung</label>
              <textarea
                id="fahrzeugbeschreibung"
                {...register("fahrzeugbeschreibung", {
                  required: "Fahrzeugbeschreibung is required",
                })}
              ></textarea>
              {errors.fahrzeugbeschreibung && (
                <p className="error">{errors.fahrzeugbeschreibung.message}</p>
              )}
            </div>
            <UploadField register={register} errors={errors} />
            <button type="submit">Absenden</button>
          </form>
        </div>
      </Fade>
    );
  };

  return (
    <div className="tarraf-landing-page">
      <Fade direction="down" triggerOnce>
        <div className="banner-container" id="home">
          <header className="header-container">
            <img src={logo} className="header-logo" alt="logo" />
            <nav className="nav-links">
              <ul className="header-nav">
                <li
                  className={`header-nav-item ${
                    activeLink === "#home" ? "active" : ""
                  }`}
                >
                  <a href="#home" onClick={() => handleSetActive("#home")}>
                    HOME
                  </a>
                </li>
                <li
                  className={`header-nav-item ${
                    activeLink === "#uber-uns" ? "active" : ""
                  }`}
                >
                  <a
                    href="#uber-uns"
                    onClick={() => handleSetActive("#uber-uns")}
                  >
                    ÜBER UNS
                  </a>
                </li>
                <li
                  className={`header-nav-item ${
                    activeLink === "#dienstleistungen" ? "active" : ""
                  }`}
                >
                  <a
                    href="#dienstleistungen"
                    onClick={() => handleSetActive("#dienstleistungen")}
                  >
                    DIENSTLEISTUNGEN
                  </a>
                </li>

                <li
                  className={`header-nav-item ${
                    activeLink === "#verkaufen" ? "active" : ""
                  }`}
                >
                  <a
                    href="#verkaufen"
                    onClick={() => handleSetActive("#verkaufen")}
                  >
                    VERKAUFEN
                  </a>
                </li>
              </ul>
            </nav>
          </header>
          <div className="banner-content">
            <div className="banner-text">
              <h1>
                Finden Sie <br />
                mühelos Ihr nächstes
                <br /> Premium-Fahrzeug
              </h1>
              <p>
                Entdecken Sie eine große Auswahl an Neu- und Gebrauchtwagen,
                vergleichen Sie Preise und fahren Sie mit dem perfekten Fahrzeug
                davon - alles an einem Ort.
              </p>
              <a className="button" href="#kontakt">
                Jetzt Auto verkaufen
              </a>
            </div>
            <div className="banner-image">
              <Slide direction="right" triggerOnce>
                <img src={heroImage} alt="Auto verkaufen" />
              </Slide>
            </div>
          </div>

          <Fade triggerOnce>
            <div className="marke mw-1400">
              <Slide direction="up" triggerOnce cascade>
                <div className="marke-container">
                  {markeData.map((item, index) => (
                    <div className="marke-item" key={index}>
                      <img src={item.image} alt={item.name} />
                      <p>{item.name}</p>
                    </div>
                  ))}
                </div>
              </Slide>
                          <a
              className="whatsapp-btn"
              href="https://wa.me/+41788306655"
              target="_blank"
              rel="noreferrer"
            >
              <img src={whatsapp_icon} alt="whatsapp-icon" />
            </a>
            </div>
          </Fade>
        </div>
      </Fade>
      <Fade triggerOnce>
        <div className="funktioniert mw-1200">
          <h1>Hauptfunktionen</h1>
          <p>
            Entdecken Sie die herausragenden Funktionen, die unsere Plattform zur 
            intelligentesten Art machen, Ihr nächstes Auto zu finden, zu vergleichen und zu kaufen.
          </p>
          <div className="funktioniert-details" id="dienstleistungen">
            <Slide direction="up" triggerOnce cascade>
              <div className="funktioniert-details-cards">
                {howItWorksData.map((item, index) => (
                  <div className="funktioniert-card" key={index}>
                    <div className="funktioniert-card-img-container">
                      <img
                        className="funktioniert-card-img"
                        src={item.image}
                        alt={item.alt}
                      />
                    </div>
                    <div className="funktioniert-card-desc">
                      <h3>{item.title}</h3>
                      <p>{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Slide>
          </div>
        </div>
      </Fade>

      <Fade triggerOnce>
        <div className="why-us" id="uber-uns" ref={whyUsRef}>
          <Slide direction="up" triggerOnce>
            <div className="why-us-cards mw-1200">
              {whyUsData.map((item, index) => (
                <div className="why-us-card" key={index}>
                  <h3>
                    <Counter end={item.title} inView={whyUsInView} />+
                  </h3>
                  <h4>{item.subtitle}</h4>
                  <p>{item.description}</p>
                </div>
              ))}
            </div>
          </Slide>
        </div>
      </Fade>

      <div className="form" id="kontakt">
        <Slide direction="up" triggerOnce>
          <h1>Verkaufen Sie Ihr Auto jetzt</h1>
          <p>
            Fangen Sie noch heute an und verwandeln Sie Ihr Auto schnell in
            Bargeld. Füllen Sie das Formular aus und erhalten Sie sofort ein
            Angebot von <strong>A&H Autopark!!</strong>
          </p>
          {renderForm()}
        </Slide>
      </div>

      <Fade triggerOnce>
        <div className="verkaufen" id="verkaufen">
          <h1>Verkaufen Sie Ihr Auto schnell</h1>
          <p>
            Erhalten Sie ein sofortiges Angebot und nehmen Sie in wenigen
            einfachen Schritten Kontakt mit A&H Autopark auf!
          </p>
          <a className="button" href="#kontakt">
            Angebot einholen
          </a>
        </div>
      </Fade>

      <Fade triggerOnce>
        <footer className="footer">
          <div className="footer-content">
            <img src={whiteLogo} alt="logo" />
            <div className="footer-links">
              <a href="#home">HOME</a>
              <a href="#uber-uns">ÜBER UNS</a>
              <a href="#dienstleistungen">DIENSTLEISTUNGEN</a>
              <a href="#verkaufen">VERKAUFEN</a>
            </div>
          </div>
        </footer>
      </Fade>
    </div>
  );
}

export default App;
